import React, { createContext, useContext, useEffect } from 'react';
import {
  type QueryObserverResult,
  type RefetchOptions,
} from '@tanstack/react-query';

import { useApiQuery } from '@shared-runtime/api/queryHelpers';
import {
  fetchFeatureControl,
  type FeatureControlResponseData,
} from '@shared-runtime/modules/feature';
import { setUser } from '@shared-web/telemetry/client';

interface FeatureControlContextValue {
  features: FeatureControlResponseData | null;
  loading: boolean;
  refetch: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<FeatureControlResponseData, Error>>;
}

/**
 * Default value for FeatureControlContext when used outside of a provider.
 * This allows OrderFormV2 and other widgets to work in isolation without requiring
 * the full provider tree (e.g., in test pages or widget development).
 */
const defaultFeatureControlValue: FeatureControlContextValue = {
  features: null,
  loading: false,
  refetch: () =>
    Promise.resolve({
      data: undefined,
    } as unknown as QueryObserverResult<FeatureControlResponseData, Error>),
};

const FeatureControlContext = createContext<FeatureControlContextValue>(
  defaultFeatureControlValue,
);

interface FeatureControlProviderProps {
  children: React.ReactNode;
}

export function FeatureControlProvider({
  children,
}: FeatureControlProviderProps) {
  const body = { platform: 'web' as const };

  const { data, isLoading, refetch, error } = useApiQuery(fetchFeatureControl, {
    body,
  });

  const features: FeatureControlResponseData | null = data || null;

  // Sync user context (UUID and IP) with OpenTelemetry which used by tracer and logger
  // FeatureControl always return the ip, so we don't need to check if the ip is null or logout scenario
  useEffect(() => {
    setUser({
      uuid: features?.userState?.userInfo?.uuid,
      ip: features?.userState?.ip,
    });
  }, [features?.userState?.userInfo?.uuid, features?.userState?.ip]);

  if (error) {
    console.error('Failed to fetch featureControl', error.message);
  }

  const value: FeatureControlContextValue = {
    features,
    loading: isLoading,
    refetch,
  };

  return (
    <FeatureControlContext.Provider value={value}>
      {children}
    </FeatureControlContext.Provider>
  );
}

export function useFeatureControlContext(): FeatureControlContextValue {
  return useContext(FeatureControlContext);
}
